import {
    CommanderFormProps,
    DateField,
    SchemaFormDefinition,
    SchemaFormFields,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { createZodFormResolver, getBase64, getDefaults, lt } from '@mews/framework';
import { ReservationContractAddParameters, ReservationContractSignatureStatus } from '@mews-commander/data-access';
import { useCalendarService } from '@mews-commander/ui';
import { StructuredDate } from '@optimus-web/core';
import { SUPPORTED_FILE_EXTENSIONS } from '../common/constants';
import { SIGNATURE_STATUS_OPTIONS } from './constants';
import { useAddContractMutation } from './documentsTable/hooks/useAddContractMutation';
import { UploadFileDialogFormValues, uploadFileSchema } from './schema';
import { useReservationContracts } from './documentsTable/hooks/useReservationContracts';
import { useDocumentsTableContext } from './documentsTable/DocumentsTableContext';

const uploadFileFormDefinition = ({
    signatureStatus,
    maxDate,
}: {
    signatureStatus?: ReservationContractSignatureStatus;
    maxDate: StructuredDate;
}): SchemaFormDefinition<ReturnType<typeof uploadFileSchema>> => ({
    fields: {
        file: {
            label: lt('File'),
            field: { type: 'file', accept: SUPPORTED_FILE_EXTENSIONS },
        },
        name: {
            label: lt('Name'),
        },
        signatureStatus: {
            label: lt('ContractsTableSignatureStatusLabel'),
            field: {
                type: 'select',
                options: SIGNATURE_STATUS_OPTIONS,
            },
        },
        signatureDate: {
            label: lt('ContractsTableSignatureDateLabel'),
            render: (props) =>
                signatureStatus === ReservationContractSignatureStatus.Signed ? (
                    <DateField mode="iso" maxDate={maxDate} {...props} />
                ) : null,
        },
        expirationDate: {
            label: lt('ExpirationDate'),
            field: { type: 'date', mode: 'iso' },
        },
        notes: {
            label: lt('Notes'),
            field: {
                type: 'textarea',
                rows: 1,
            },
        },
    },
});

export const UploadFileDialogForm = (props: CommanderFormProps) => {
    const { groupId, reservationId } = useDocumentsTableContext();
    const contracts = useReservationContracts({ groupId, reservationId });
    const contractNames = contracts?.map((contract) => contract.name) ?? [];
    const calendarService = useCalendarService();
    const today = calendarService.getCurrentDate();
    const { mutateAsync: addContractMutation } = useAddContractMutation();
    const { renderFormContent, watch } = useHandledDialogFormControls<UploadFileDialogFormValues>({
        ...props,
        defaultValues: getDefaults(uploadFileSchema(contractNames)),
        resolver: createZodFormResolver(uploadFileSchema(contractNames)),
        onSubmit: async (values) => {
            const base64Data = await getBase64(values.file[0]);
            const parameters: ReservationContractAddParameters = {
                reservationId,
                file: {
                    name: values.file[0].name,
                    mimeType: values.file[0].type,
                    data: base64Data,
                },
                name: values.name,
                signatureStatus: values.signatureStatus,
                signatureDate:
                    values.signatureStatus === ReservationContractSignatureStatus.Signed
                        ? values.signatureDate
                        : undefined,
                expirationDate: values.expirationDate,
                notes: values.notes,
            };
            await addContractMutation(parameters);
        },
    });
    const signatureStatus = watch('signatureStatus');

    return renderFormContent(
        <SchemaFormFields
            schema={uploadFileSchema(contractNames)}
            formDefinition={uploadFileFormDefinition({ signatureStatus, maxDate: today })}
        />
    );
};
